import { reactive } from 'vue'
import { io } from 'socket.io-client'

import useToast from '@/hooks/useToast'

export const OFFLINE = 'offline'
export const READY = 'ready'
export const CONNECTING = 'connecting'
export const CREATING_ROOM = 'creating'
export const JOINING_ROOM = 'joining'
export const JOINED = 'joined'

const serverUrl = process.env.NODE_ENV === 'development'
  ? 'http://localhost:3000'
  : 'https://dungeonexpress-services.twocatmoon.com'

let hostState
let hostSocket
let hostHandlers = []

let joinState
let joinSocket
let joinHandlers = []

function checkHostCache () {
  return localStorage.getItem('__com.jordanranson.dungeonexpress__hostCode')
}

function clearHostCache () {
  localStorage.removeItem('__com.jordanranson.dungeonexpress__hostCode')
}

function cacheHostCode (code) {
  localStorage.setItem('__com.jordanranson.dungeonexpress__hostCode', code)
}

function checkJoinCache () {
  const obj = {
    code: localStorage.getItem('__com.jordanranson.dungeonexpress__joinCode'),
    characterId: localStorage.getItem('__com.jordanranson.dungeonexpress__joinCharacterId')
  }
  if (!obj.code || !obj.characterId) return null
  return obj
}

function clearJoinCache () {
  localStorage.removeItem('__com.jordanranson.dungeonexpress__joinCode')
}

function cacheJoinRequest (code, characterId) {
  localStorage.setItem('__com.jordanranson.dungeonexpress__joinCode', code)
  localStorage.setItem('__com.jordanranson.dungeonexpress__joinCharacterId', characterId)
}

export function useHost () {
  const toast = useToast()

  const state = hostState
    ? hostState
    : reactive({
      status: OFFLINE,
      characters: [],
      rolls: [],
      npcs: [],
      combatOrder: [],
      notes: '',
      code: null
    })
  hostState = state

  const trigger = (eventName, eventData) => {
    const eventhostHandlers = hostHandlers.filter((h) => h.eventName === eventName)
    eventhostHandlers.forEach((h) => h.handler(eventData))
  }

  const disconnect = () => {
    hostSocket.offAny()
    hostSocket.disconnect()
    hostSocket = null
    state.status = OFFLINE
    state.characters = []
    state.rolls = []
    state.npcs = []
    state.combatOrder = []
    state.notes = ''
    state.code = null
  }

  return {
    state,

    on (eventName, handler) {
      hostHandlers.push({ eventName, handler })
    },

    off (handler) {
      hostHandlers = hostHandlers.filter(((h) => h.handler !== handler))
    },

    findCachedRoom () {
      const cachedCode = checkHostCache()
      if (!cachedCode) return false
      return cachedCode
    },

    connect () {
      return new Promise((resolve, reject) => {
        hostSocket = io(serverUrl, { transports: ['websocket'] })
        state.status = CONNECTING

        hostSocket.on('connect', () => {
          state.status = READY
          resolve()
        })

        hostSocket.on('connect_error', (err) => {
          toast(`Connection error: "${err.message}".`)
          trigger('error', err.message)
          disconnect()
          clearHostCache()
          reject(err.message)
        })

        hostSocket.on('disconnect', (reason) => {
          toast(`Disconnected: "${reason}".`)
          trigger('disconnect', reason)
          disconnect()
          reject(reason)
        })

        hostSocket.on('sInvalidRoom', () => {
          toast('Invalid room.')
          trigger('error', 'Invalid room.')
          disconnect()
          reject('Invalid room.')
          clearHostCache()
        })

        hostSocket.on('sInvalidCode', () => {
          trigger('error', 'invalid code')
          disconnect()
          reject('invalid code')
          clearHostCache()
        })

        hostSocket.on('sRoomCreated', ({ room }) => {
          toast('Room created.')
          for (const key in room) {
            if (state[key] !== undefined) state[key] = room[key]
          }
          state.status = JOINED
          trigger('roomCreated', room)
          cacheHostCode(room.code)
        })

        hostSocket.on('sHostRoomRejoined', ({ room }) => {
          toast('Connected.')
          for (const key in room) {
            if (state[key] !== undefined) state[key] = room[key]
          }
          state.status = JOINED
          trigger('roomRejoined', room)
        })

        hostSocket.on('sRoomClosed', ({ reason }) => {
          toast('Room closed: ' + reason)
          trigger('roomClosed', reason)
          clearHostCache()
          disconnect()
        })

        hostSocket.on('sCharacterJoinedRoom', ({ character, characters, combatOrder }) => {
          toast(`${character.name} joined the room.`)
          state.characters = characters
          state.combatOrder = combatOrder
        })

        hostSocket.on('sCharacterRejoinedRoom', ({ characters, combatOrder }) => {
          state.characters = characters
          state.combatOrder = combatOrder
        })

        hostSocket.on('sCharacterLeftRoom', ({ characterId, characters, combatOrder }) => {
          const character = state.characters.find((c) => c.id === characterId)
          toast(`${character.name} left the room.`)
          state.characters = characters
          state.combatOrder = combatOrder
        })

        hostSocket.on('sAddNpc', ({ npcs, combatOrder }) => {
          state.npcs = npcs
          state.combatOrder = combatOrder
        })
  
        hostSocket.on('sUpdateNpc', ({ npc }) => {
          const index = state.npcs.findIndex((n) => n.id === npc.id)
          if (index < 0) return
          state.npcs.splice(index, 1, npc)
        })
  
        hostSocket.on('sResetCombatOrder', ({ combatOrder }) => {
          state.npcs = []
          state.combatOrder = combatOrder
        })
  
        hostSocket.on('sUpdateCombatOrder', ({ combatOrder }) => {
          state.combatOrder = combatOrder
        })

        hostSocket.on('sUpdateRolls', ({ rolls }) => {
          state.rolls = rolls
        })

        hostSocket.on('sUpdateCharacter', ({ character }) => {
          console.log('updateCharacter', character)
          // const index = state.characters.findIndex((c) => c.id === character.id)
          // if (index < 0) return
          // state.characters.splice(index, 1, character)
          const found = state.characters.find((c) => c.id === character.id)
          if (!found) return

          for (let key in character) {
            found[key] = character[key]
          }
        })

        hostSocket.on('sGiveInspiration', ({ characterId }) => {
          trigger('giveInspiration', characterId)
        })

        hostSocket.on('sGiveEquipment', ({ characterId, itemId, qty }) => {
          trigger('giveEquipment', { characterId, itemId, qty })
        })
      })
    },

    disconnect,

    createRoom () {
      state.status = CREATING_ROOM
      hostSocket.emit('cHostCreateRoom')
    },

    rejoinRoom (code) {
      state.status = CREATING_ROOM
      hostSocket.emit('cHostRejoinRoom', { code })
    },

    closeRoom () {
      hostSocket.emit('cHostCloseRoom')
      disconnect()
      clearHostCache()
    },

    addNpc (npc) {
      hostSocket.emit('cHostAddNpc', { npc })
    },

    updateNpc (npc) {
      hostSocket.emit('cHostUpdateNpc', { npc })
    },

    resetCombatOrder () {
      hostSocket.emit('cHostResetCombatOrder')
    },

    updateCombatOrder (combatOrder) {
      hostSocket.emit('cHostUpdateCombatOrder', { combatOrder })
    },

    giveInspiration (characterId) {
      hostSocket.emit('cHostGiveInspiration', { characterId })
    },

    giveEquipment (characterId, itemId, item, qty) {
      hostSocket.emit('cHostGiveEquipment', { characterId, itemId, item, qty })
    },

    clearRolls () {
      hostSocket.emit('cHostClearRolls')
    }
  }
}

export function useJoin () {
  const toast = useToast()

  const state = joinState 
    ? joinState
    : reactive({
      status: OFFLINE,
      characters: [],
      npcs: [],
      combatOrder: [],
      code: null,
      characterId: null
    })
  joinState = state

  let debounceUpdateCharacter

  const trigger = (eventName, eventData) => {
    const eventjoinHandlers = joinHandlers.filter((h) => h.eventName === eventName)
    eventjoinHandlers.forEach((h) => h.handler(eventData))
  }

  const disconnect = () => {
    joinSocket.offAny()
    joinSocket.disconnect()
    joinSocket = null
    state.status = OFFLINE
    state.characters = []
    state.npcs = []
    state.combatOrder = []
    state.code = null
    state.characterId = null
  }

  return {
    state,

    on (eventName, handler) {
      joinHandlers.push({ eventName, handler })
    },

    off (handler) {
      joinHandlers = joinHandlers.filter(((h) => h.handler !== handler))
    },

    findCachedRoom () {
      const cachedCode = checkJoinCache()
      if (!cachedCode) return false
      return cachedCode
    },

    connect () {
      return new Promise((resolve, reject) => {
        joinSocket = io(serverUrl, { transports: ['websocket'] })
        state.status = CONNECTING
        
        joinSocket.on('connect', () => {
          state.status = READY
          resolve()
        })

        joinSocket.on('connect_error', (err) => {
          toast('Connection error: ' + err.message)
          trigger('error', err)
          disconnect()
          clearJoinCache()
          reject(err.message)
        })

        joinSocket.on('disconnect', (reason) => {
          toast(`Disconnected: "${reason}".`)
          disconnect()
          reject(reason)
        })

        joinSocket.on('sInvalidRoom', () => {
          toast('Invalid room.')
          trigger('error', 'invalid room')
          disconnect()
          reject('invalid room')
          clearJoinCache()
        })

        joinSocket.on('sInvalidCharacter', () => {
          toast('Invalid character.')
          trigger('error', 'invalid character')
          disconnect()
          reject('invalid character')
          clearJoinCache()
        })

        joinSocket.on('sInvalidCode', () => {
          toast('Invalid code.')
          trigger('error', 'invalid code')
          disconnect()
          reject('invalid code')
          clearJoinCache()
        })
        
        joinSocket.on('sRoomClosed', ({ reason }) => {
          toast('Room closed: ' + reason)
          disconnect()
          clearJoinCache()
        })

        joinSocket.on('sRoomJoined', ({ room }) => {
          toast('Joined the room.')
          for (const key in room) {
            if (state[key] !== undefined) state[key] = room[key]
          }
          state.status = JOINED
          cacheJoinRequest(room.code, state.characterId)
        })

        joinSocket.on('sRoomRejoined', ({ room }) => {
          toast('Connected.')
          for (const key in room) {
            if (state[key] !== undefined) state[key] = room[key]
          }
          state.status = JOINED
          trigger('roomRejoined', room)
        })

        joinSocket.on('sRoomLeft', () => {
          toast('Left the room.')
          disconnect()
        })

        joinSocket.on('sCharacterJoinedRoom', ({ character, characters, combatOrder }) => {
          toast(`${character.name} joined the room.`)
          state.characters = characters
          state.combatOrder = combatOrder
        })

        joinSocket.on('sCharacterRejoinedRoom', ({ characters, combatOrder }) => {
          state.characters = characters
          state.combatOrder = combatOrder
        })

        joinSocket.on('sCharacterLeftRoom', ({ characterId, characters, combatOrder }) => {
          const character = state.characters.find((c) => c.id === characterId)
          toast(`${character.name} left the room.`)
          state.characters = characters
          state.combatOrder = combatOrder
        })

        joinSocket.on('sAddNpc', ({ npcs, combatOrder }) => {
          state.npcs = npcs
          state.combatOrder = combatOrder
        })

        joinSocket.on('sUpdateNpc', ({ npc }) => {
          const index = state.npcs.findIndex((n) => n.id === npc.id)
          if (index < 0) return
          state.npcs.splice(index, 1, npc)
        })

        joinSocket.on('sResetCombatOrder', ({ combatOrder }) => {
          state.npcs = []
          state.combatOrder = combatOrder
        })

        joinSocket.on('sUpdateCombatOrder', ({ combatOrder }) => {
          state.combatOrder = combatOrder
        })

        joinSocket.on('sUpdateCharacter', ({ character }) => {
          // const index = state.characters.findIndex((c) => c.id === character.id)
          // if (index < 0) return
          // state.characters.splice(index, 1, character)
          const found = state.characters.find((c) => c.id === character.id)
          if (!found) return

          for (let key in character) {
            found[key] = character[key]
          }
        })

        joinSocket.on('sGiveInspiration', ({ characterId }) => {
          trigger('giveInspiration', characterId)
        })

        joinSocket.on('sGiveEquipment', ({ characterId, itemId, qty }) => {
          trigger('giveEquipment', { characterId, itemId, qty })
        })
      })
    },

    disconnect,

    joinRoom (code, character) {
      state.status = JOINING_ROOM
      state.characterId = character.id
      joinSocket.emit('cJoinRoom', { code, character })
    },

    rejoinRoom (code, character) {
      state.status = JOINING_ROOM
      state.characterId = character.id
      joinSocket.emit('cRejoinRoom', { code, character })
    },

    leaveRoom () {
      joinSocket.emit('cLeaveRoom')
      disconnect()
      clearJoinCache()
    },

    roll (outcome) {
      joinSocket.emit('cRoll', { outcome })
    },

    updateCharacter (character) {
      clearTimeout(debounceUpdateCharacter)
      debounceUpdateCharacter = setTimeout(() => {
        joinSocket.emit('cUpdateCharacter', { character })
      }, 1000)
    }
  }
}
